/**
 * Materialise an EditState back into a FitFile: rebuild the length records, rewrite the lap
 * and session derived fields, renumber message_index / first_length_index, and drop the
 * global-113 best-effort records when lengths were structurally edited.
 *
 * The length block is contiguous in the stream (verified on all fixtures), so it is spliced
 * as a unit; laps are replaced in place by template identity. Records we don't touch pass
 * through verbatim — a length/lap/session record is marked dirty only if a field actually
 * changed, which is what gives byte-identity on the unedited parts and confines fill-all to
 * the laps that changed.
 */
import type { DataRecord, FitFile, FitRecord } from '../codec/types'
import { GLOBAL, LAP, LEN, LENGTH_TYPE, SES } from './fields'
import { R, recomputeLap, recomputeLength, recomputeSession, type FieldMap } from './recompute'
import type { EditLength, EditState, EditStint } from './edit'
import type { Length, Stint } from './model'

export type DerivedPolicy = 'garmin-native' | 'fill-all'

export interface MaterializeOptions {
  readonly derivedPolicy?: DerivedPolicy
}

export function materialize(state: EditState, options: MaterializeOptions = {}): FitFile {
  const policy = options.derivedPolicy ?? 'garmin-native'
  const poolCm = state.poolLengthCm

  // Assign ordinals across the whole activity, then per stint.
  let ordinal = 0
  const modelLengths: Length[] = []
  const modelStints: { model: Stint; edit: EditStint; lapRecord: DataRecord }[] = []

  state.stints.forEach((stint, stintIndex) => {
    const lengthRecords: DataRecord[] = []
    const members: Length[] = []
    for (const editLength of stint.lengths) {
      const record = buildLengthRecord(editLength, ordinal, poolCm)
      lengthRecords.push(record)
      const model = toModelLength(editLength, record, ordinal)
      members.push(model)
      modelLengths.push(model)
      ordinal += 1
    }
    const lapRecord = buildLapRecord(stint, members, stintIndex, poolCm, policy)
    modelStints.push({
      model: { lapRecord, lengths: members, isRest: members.every((l) => !l.active) },
      edit: stint,
      lapRecord,
    })
    // Keep the built length records alongside for splicing.
    lapToLengths.set(lapRecord, lengthRecords)
  })

  const sessionRecord = buildSessionRecord(state, modelLengths, poolCm, policy)

  return assemble(state, modelStints, sessionRecord)
}

// Scratch map: new lap record -> its new length records, used only within a materialize call.
const lapToLengths = new WeakMap<DataRecord, DataRecord[]>()

function toModelLength(editLength: EditLength, record: DataRecord, ordinal: number): Length {
  return {
    ordinal,
    record,
    active: editLength.active,
    durationMs: editLength.durationMs,
    startTime: editLength.startTime,
    strokes: editLength.strokes,
    avgSpeed: activeAvgSpeed(editLength, record),
    swimStroke: editLength.swimStroke,
  }
}

function activeAvgSpeed(editLength: EditLength, record: DataRecord): number {
  if (!editLength.active) return 0
  const i = record.def.fields.findIndex((f) => f.fieldDefNum === LEN.avgSpeed)
  const v = i < 0 ? 0 : record.values[i]
  return typeof v === 'number' ? v : 0
}

function buildLengthRecord(editLength: EditLength, ordinal: number, poolCm: number): DataRecord {
  const record = cloneData(editLength.template)
  set(record, LEN.messageIndex, ordinal)
  set(record, LEN.startTime, editLength.startTime)
  set(record, LEN.totalElapsed, editLength.durationMs)
  set(record, LEN.totalTimer, editLength.durationMs)
  if (editLength.active) {
    set(record, LEN.totalStrokes, editLength.strokes)
    set(record, LEN.lengthType, LENGTH_TYPE.active)
    set(record, LEN.swimStroke, editLength.swimStroke)
    applyMap(record, recomputeLength(mkLen(editLength, ordinal), poolCm))
  }
  return finalize(record, editLength.template)
}

function mkLen(editLength: EditLength, ordinal: number): Length {
  return {
    ordinal,
    record: editLength.template,
    active: editLength.active,
    durationMs: editLength.durationMs,
    startTime: editLength.startTime,
    strokes: editLength.strokes,
    avgSpeed: 0,
    swimStroke: editLength.swimStroke,
  }
}

function buildLapRecord(
  stint: EditStint,
  members: Length[],
  stintIndex: number,
  poolCm: number,
  policy: DerivedPolicy,
): DataRecord {
  const record = cloneData(stint.lapTemplate)
  const modelStint: Stint = {
    lapRecord: record,
    lengths: members,
    isRest: members.every((l) => !l.active),
  }
  set(record, LAP.messageIndex, stintIndex)
  const derived = recomputeLap(modelStint, poolCm)
  applyMap(record, derived)
  if (policy === 'fill-all') applyMap(record, lapFillAll(modelStint, poolCm, derived))
  return finalize(record, stint.lapTemplate)
}

function lapFillAll(stint: Stint, poolCm: number, derived: FieldMap): FieldMap {
  const strokes = derived[LAP.totalCycles]!
  const nAct = derived[LAP.numActiveLengths]!
  if (nAct === 0 || strokes === 0) return {}
  return {
    [LAP.avgSpeedLegacy]: derived[LAP.enhAvgSpeed]!,
    [LAP.maxSpeedLegacy]: derived[LAP.enhMaxSpeed]!,
    [LAP.avgStrokeDistance]: R(((nAct * (poolCm / 100)) / strokes) * 100),
  }
}

function buildSessionRecord(
  state: EditState,
  lengths: Length[],
  poolCm: number,
  policy: DerivedPolicy,
): DataRecord {
  const record = cloneData(state.originalFile.records.find(isSession)! as DataRecord)
  const activity = {
    file: state.originalFile,
    session: record,
    poolLengthCm: poolCm,
    lengths,
    stints: [],
  }
  const derived = recomputeSession(activity)
  applyMap(record, derived)
  if (policy === 'fill-all') applyMap(record, sessionFillAll(derived, poolCm))
  return finalize(record, state.originalFile.records.find(isSession)! as DataRecord)
}

function sessionFillAll(derived: FieldMap, poolCm: number): FieldMap {
  const strokes = derived[SES.totalCycles]!
  const nAct = derived[SES.numActiveLengths]!
  if (nAct === 0 || strokes === 0) return {}
  return {
    [SES.avgSpeedLegacy]: derived[SES.enhAvgSpeed]!,
    [SES.maxSpeedLegacy]: derived[SES.enhMaxSpeed]!,
    [SES.avgStrokeDistance]: R(((nAct * (poolCm / 100)) / strokes) * 100),
  }
}

/** Rebuild the record array: splice the new length block, swap laps and session, drop g113. */
function assemble(
  state: EditState,
  stints: { lapRecord: DataRecord }[],
  sessionRecord: DataRecord,
): FitFile {
  const original = state.originalFile.records
  const lapByTemplate = new Map<DataRecord, DataRecord>()
  state.stints.forEach((s, i) => lapByTemplate.set(s.lapTemplate, stints[i]!.lapRecord))
  const sessionTemplate = original.find(isSession) as DataRecord

  const newLengths = stints.flatMap((s) => lapToLengths.get(s.lapRecord)!)

  const out: FitRecord[] = []
  let lengthBlockEmitted = false

  for (const record of original) {
    if (record.kind === 'data' && record.def.globalMessageNumber === GLOBAL.length) {
      if (!lengthBlockEmitted) {
        out.push(...newLengths)
        lengthBlockEmitted = true
      }
      continue // drop every original length record; the block is replaced as a unit
    }
    if (record.kind === 'data' && record.def.globalMessageNumber === GLOBAL.lap) {
      const replacement = lapByTemplate.get(record)
      out.push(replacement ?? record)
      continue
    }
    if (record.kind === 'data' && record === sessionTemplate) {
      out.push(sessionRecord)
      continue
    }
    if (
      state.lengthsTouched &&
      record.kind === 'data' &&
      record.def.globalMessageNumber === GLOBAL.bestEffort
    ) {
      continue // drop global-113 best-effort records when lengths were structurally edited
    }
    out.push(record)
  }

  return { header: state.originalFile.header, records: out }
}

// --- record helpers -------------------------------------------------------------------------

function isSession(record: FitRecord): boolean {
  return record.kind === 'data' && record.def.globalMessageNumber === GLOBAL.session
}

function cloneData(record: DataRecord): DataRecord {
  return {
    kind: 'data',
    def: record.def,
    values: record.values.slice(),
    devValues: record.devValues,
    compressed: record.compressed,
    timeOffset: record.timeOffset,
    raw: record.raw,
    dirty: false,
  }
}

/** Set a field on a clone if present; silently skip if the record's definition lacks it. */
function set(record: DataRecord, fieldDefNum: number, value: number): void {
  const i = record.def.fields.findIndex((f) => f.fieldDefNum === fieldDefNum)
  if (i >= 0) record.values[i] = value
}

function applyMap(record: DataRecord, map: FieldMap): void {
  for (const [field, value] of Object.entries(map)) set(record, Number(field), value)
}

/** Mark dirty iff any scalar value changed from the template; otherwise keep it a passthrough. */
function finalize(record: DataRecord, template: DataRecord): DataRecord {
  let changed = false
  for (let i = 0; i < record.values.length; i++) {
    if (record.values[i] !== template.values[i]) {
      changed = true
      break
    }
  }
  record.dirty = changed
  return record
}
